'use client';

import Link from 'next/link';
import { Instagram, Facebook, Youtube } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { buildLocalePath, type Locale } from '@/i18n/config';
import { useCookieConsentReset } from '@/components/consent/CookieConsentBanner';

export interface FooterLink {
  label: string;
  href: string;
  target?: string;
  rel?: string;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

export interface PublicFooterProps {
  tenantName?: string;
  sections?: FooterSection[];
  copyright?: string;
}

// Footer sections - labels use translation keys, resolved at render time
// Each label corresponds to a nav.* key in the i18n messages
interface FooterSectionConfig {
  titleKey: string; // footer.* key for section title
  links: { labelKey: string; href: string; target?: string; rel?: string }[];
}

const defaultSectionConfigs: FooterSectionConfig[] = [
  {
    titleKey: 'theRide',
    links: [
      { labelKey: 'aboutDgd', href: '/about' },
      { labelKey: 'howItWorks', href: '/how-it-works' },
      { labelKey: 'mensHealth', href: '/mens-health' },
      { labelKey: 'vehicleEligibility', href: '/vehicles' },
      { labelKey: 'dapperGuide', href: '/dapper' },
      { labelKey: 'carClubInformation', href: '/club-information' },
    ],
  },
  {
    titleKey: 'event',
    links: [
      { labelKey: 'findADrive', href: '/events' },
      { labelKey: 'hostADrive', href: '/hosting' },
      { labelKey: 'globalMap', href: '/map' },
      { labelKey: 'register', href: '/register' },
      { labelKey: 'leaderboards', href: '/leaderboard' },
    ],
  },
  {
    titleKey: 'community',
    links: [
      { labelKey: 'gallery', href: '/gallery' },
      { labelKey: 'contentUpload', href: '/content-upload' },
      { labelKey: 'mediaAssets', href: '/media-assets' },
      { labelKey: 'rewards', href: '/rewards' },
    ],
  },
  {
    titleKey: 'support',
    links: [
      { labelKey: 'contactUs', href: '/contact' },
      { labelKey: 'faqs', href: '/faq' },
    ],
  },
  {
    titleKey: 'legal',
    links: [
      { labelKey: 'privacyPolicy', href: '/privacy' },
      { labelKey: 'termsOfService', href: '/terms' },
    ],
  },
];

export function PublicFooter({
  tenantName = "The Distinguished Gentleman's Drive",
  sections: sectionsProp,
  copyright,
}: PublicFooterProps) {
  const locale = useLocale() as Locale;
  const t = useTranslations('footer');
  const tNav = useTranslations('nav');
  const tCommon = useTranslations('common');
  const currentYear = new Date().getFullYear();
  const resetCookieConsent = useCookieConsentReset();

  // Build translated sections from config if no custom sections provided
  const sections: FooterSection[] =
    sectionsProp ??
    defaultSectionConfigs.map((config) => ({
      title: t(config.titleKey as 'theRide'),
      links: config.links.map((link) => ({
        label: tNav(link.labelKey as 'aboutDgd'),
        href: link.href.startsWith('#') ? link.href : buildLocalePath(locale, link.href),
        target: link.target,
        rel: link.rel,
      })),
    }));

  return (
    <footer className="bg-dgd-navy text-dgd-cream">
      {/* Follow Us / Get The App */}
      <div className="border-b border-dgd-cream/15">
        <div className="container py-6">
          <div className="flex flex-col items-center gap-8 sm:flex-row sm:items-start sm:justify-center sm:gap-24 lg:gap-32">
            <div className="flex flex-col items-center">
              <h3 className="text-[10px] uppercase tracking-widest text-dgd-cream/50 text-center mb-5">
                {t('followUs')}
              </h3>
              <div className="flex items-center justify-center gap-5 h-10">
                <a
                  href="https://instagram.com/gentlemansdrive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dgd-cream/70 hover:text-dgd-gold transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-6 h-6" />
                </a>
                <a
                  href="https://facebook.com/gentlemansdrive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dgd-cream/70 hover:text-dgd-gold transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-6 h-6" />
                </a>
                <a
                  href="https://youtube.com/gentlemansdrive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dgd-cream/70 hover:text-dgd-gold transition-colors"
                  aria-label="YouTube"
                >
                  <Youtube className="w-6 h-6" />
                </a>
                <a
                  href="https://x.com/Official_DGD"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dgd-cream/70 hover:text-dgd-gold transition-colors"
                  aria-label="X"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@gentlemansdrive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dgd-cream/70 hover:text-dgd-gold transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V9.4a8.16 8.16 0 0 0 4.77 1.52V7.47a4.85 4.85 0 0 1-1.01-.78z" />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/gentlemansdrive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dgd-cream/70 hover:text-dgd-gold transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <h3 className="text-[10px] uppercase tracking-widest text-dgd-cream/50 text-center mb-5">
                {t('getTheApp')}
              </h3>
              <div className="flex items-center gap-3 h-10">
                <a
                  href="https://apps.apple.com/us/app/gentlemans-drive/id6569228734"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-dgd-cream/25 bg-dgd-cream/5 px-3 py-2 text-dgd-cream hover:bg-dgd-cream/10 transition-colors"
                  aria-label="Download on the App Store"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <div className="leading-tight text-left">
                    <div className="text-[8px] uppercase tracking-wider text-dgd-cream/70">
                      Download on the
                    </div>
                    <div className="text-sm font-semibold">App Store</div>
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.gentlemansdrive.android.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-dgd-cream/25 bg-dgd-cream/5 px-3 py-2 text-dgd-cream hover:bg-dgd-cream/10 transition-colors"
                  aria-label="Get it on Google Play"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path
                      d="M3.4 1.6c-.3.2-.4.6-.4 1v18.8c0 .4.2.7.4.9l10.6-10.6L3.4 1.6z"
                      fill="#00d4ff"
                    />
                    <path
                      d="M17.7 8.6 4.6 1.2c-.4-.2-.7-.2-1 0l10.6 10.5 3.5-3.1z"
                      fill="#00f176"
                    />
                    <path d="m17.7 15.4-3.5-3.1L3.6 22.8c.3.2.6.2 1 0l13.1-7.4z" fill="#ff3a44" />
                    <path d="m21 11-3.3-1.9-3.7 2.9 3.7 2.9 3.3-1.9c1-.6 1-2 0-2z" fill="#ffce00" />
                  </svg>
                  <div className="leading-tight text-left">
                    <div className="text-[8px] uppercase tracking-wider text-dgd-cream/70">
                      Get it on
                    </div>
                    <div className="text-sm font-semibold">Google Play</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Grid */}
      <div className="container py-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-4">
          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-dgd-cream/55 mb-3">
                {section.title}
              </h3>
              <ul className="space-y-1.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    {link.href === '#cookie-settings' ? (
                      <button
                        type="button"
                        onClick={resetCookieConsent}
                        className="text-sm text-dgd-cream/75 hover:text-dgd-gold transition-colors"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        prefetch={false}
                        href={link.href}
                        target={link.target}
                        rel={link.rel}
                        className="text-sm text-dgd-cream/75 hover:text-dgd-gold transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-dgd-cream/15">
        <div className="container py-5">
          <div className="flex items-center justify-center">
            <span className="text-xs text-dgd-cream/50">
              {copyright || t('copyright', { year: currentYear, tenantName })}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
